import { Link } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";
import { COMPANY } from "../../data/site";
import { useLang } from "../../i18n/LanguageContext";

const LOCAL = [
    { label: "Portail Genève", to: "/portail-geneve" },
    { label: "Portail Pays de Gex", to: "/portail-pays-de-gex" },
    { label: "Portail Gex", to: "/portail-gex" },
    { label: "Portail Ferney-Voltaire", to: "/portail-ferney-voltaire" },
    { label: "Portail Divonne-les-Bains", to: "/portail-divonne-les-bains" },
    { label: "Portail Saint-Genis-Pouilly", to: "/portail-saint-genis-pouilly" },
    { label: "Portail Annemasse", to: "/portail-annemasse" },
    { label: "Portail Haute-Savoie", to: "/portail-haute-savoie" },
    { label: "Brise-vue Genève", to: "/brise-vue-geneve" },
    { label: "Clôture Pays de Gex", to: "/cloture-pays-de-gex" },
];

export default function Footer() {
    const { t } = useLang();
    const FOOTER_LINKS = [
        { label: t("nav.home"), to: "/" },
        { label: t("nav.gates"), to: "/portails" },
        { label: t("nav.fences"), to: "/clotures" },
        { label: t("nav.privacy_screen"), to: "/brise-vue" },
        { label: t("nav.motorization"), to: "/motorisation-portail" },
        { label: t("nav.projects"), to: "/realisations" },
        { label: t("nav.zones"), to: "/zones-intervention" },
        { label: t("nav.contact"), to: "/contact" },
    ];
    return (
        <footer data-testid="site-footer" className="bg-[#05090C] border-t border-white/10 mt-24">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
                    <div className="md:col-span-4">
                        <div className="flex items-center gap-4 mb-5">
                            <img src={COMPANY.logo} alt="Portail du Léman" className="w-20 h-20 object-contain drop-shadow-[0_0_22px_rgba(0,194,199,0.35)]" />
                            <div>
                                <div className="font-display text-2xl text-white">Portail du Léman</div>
                                <div className="text-[10px] tracking-[0.22em] uppercase text-brand-accent/80">Établi près de Genève</div>
                            </div>
                        </div>
                        <p className="text-sm text-white/60 leading-relaxed mb-6">
                            Portails, clôtures, brise-vue et motorisations sur mesure autour de Genève, du Pays de Gex, de la Haute-Savoie et du bassin lémanique.
                        </p>
                        <div className="space-y-3 text-sm">
                            <div className="flex items-start gap-3 text-white/70">
                                <MapPin className="w-4 h-4 mt-0.5 text-brand-accent shrink-0" />
                                <div>
                                    {COMPANY.address.street}<br />
                                    {COMPANY.address.postalCode} {COMPANY.address.city}<br />
                                    {COMPANY.address.country}
                                </div>
                            </div>
                            {COMPANY.phones.map((p) => (
                                <a
                                    key={p.tel}
                                    href={`tel:${p.tel}`}
                                    data-testid={`footer-phone-${p.label.toLowerCase()}`}
                                    className="flex items-center gap-3 text-white/80 hover:text-brand-accent transition"
                                >
                                    <Phone className="w-4 h-4 text-brand-accent" />
                                    <span><span className="text-white/40 text-xs mr-2">{p.label}</span>{p.value}</span>
                                </a>
                            ))}
                            <a href={`mailto:${COMPANY.email}`} data-testid="footer-email" className="flex items-center gap-3 text-white/80 hover:text-brand-accent transition">
                                <Mail className="w-4 h-4 text-brand-accent" />
                                {COMPANY.email}
                            </a>
                        </div>
                    </div>

                    <div className="md:col-span-3">
                        <div className="text-[11px] tracking-[0.28em] uppercase text-brand-accent/80 mb-5">{t("footer.nav")}</div>
                        <ul className="space-y-2.5">
                            {FOOTER_LINKS.map((l) => (
                                <li key={l.to}>
                                    <Link to={l.to} className="text-sm text-white/65 hover:text-white link-underline">{l.label}</Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="md:col-span-5">
                        <div className="text-[11px] tracking-[0.28em] uppercase text-brand-accent/80 mb-5">{t("footer.zones")}</div>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-2.5">
                            {LOCAL.map((l) => (
                                <Link key={l.to} to={l.to} className="text-sm text-white/65 hover:text-brand-accent">
                                    {l.label}
                                </Link>
                            ))}
                        </div>
                        <div className="mt-8 text-xs text-white/40 leading-relaxed">
                            {t("footer.also")}
                        </div>
                    </div>
                </div>

                <div className="mt-14 pt-8 border-t border-white/[0.08] flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <div className="text-xs text-white/40">
                        © {new Date().getFullYear()} Portail du Léman — {COMPANY.tagline}.
                    </div>
                    <div className="flex items-center gap-6 text-xs text-white/40">
                        <Link to="/mentions-legales" className="hover:text-white">{t("footer.mentions")}</Link>
                        <Link to="/confidentialite" className="hover:text-white">{t("footer.privacy")}</Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}
